import {
  Component,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
  input,
  output,
  isDevMode,
} from '@angular/core';
import {
  ButtonLoadingDirective,
  CardComponent,
  CardContentComponent,
  IconComponent,
  AlertService,
  LoadingComponent,
  CardHeaderComponent,
} from '@gains-com/ui-angular';
import { CurrencyPipe, formatDate, NgTemplateOutlet } from '@angular/common';
import { catchError, EMPTY, exhaustMap, filter, finalize, from, map, of, switchMap, tap } from 'rxjs';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatError, MatLabel, MatPrefix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { RedemptionDialogHeaderComponent } from '../redemption-dialog-header/redemption-dialog-header.component.js';
import { AnalyticsEvent, decimal, generateId, PaymentMethodEntityTypeId } from '@gains-com/shared';
import { PaymentMethodsService } from '../../../../core/payment-methods.js';
import {
  CoinflowFeeEstimate,
  CoinflowWithdrawalSpeed,
  RedemptionsService,
} from '../../../../core/redemption.service.js';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CoinflowVenmoPaymentMethod } from '../../common.js';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Analytics } from '../../../../core/analytics/analytics.service.js';
import { ActiveRedemptionViewService } from '../../../../core/view-states/active-redemption-view.service.js';
import { UserViewService } from '../../../../core/view-states/user-view.service.js';
import { PaymentMethodsViewService } from '../../../../core/view-states/payment-methods-view.service.js';
import { AccountLimitsViewService } from '../../../../core/view-states/account-limits-view.service.js';
import { LOGGER_FACTORY } from '../../../../core/logger/logger-factory.provider.js';
import { LottieAnimationComponent } from '../../../lottie-animation/lottie-animation.component.js';
import { UsPhoneInputDirective } from '../../../../directives/us-phone-input.directive';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-venmo-redemption',
  standalone: true,
  imports: [
    CardComponent,
    CardContentComponent,
    RedemptionDialogHeaderComponent,
    MatButton,
    ButtonLoadingDirective,
    CurrencyPipe,
    FormsModule,
    ReactiveFormsModule,
    IconComponent,
    MatFormField,
    MatInput,
    MatLabel,
    MatError,
    MatPrefix,
    LottieAnimationComponent,
    LoadingComponent,
    CardHeaderComponent,
    UsPhoneInputDirective,
  ],

  templateUrl: './venmo-redemption.component.html',
  styleUrls: ['../styles.scss', './venmo-redemption.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VenmoRedemptionComponent {
  // Service Injections
  private readonly _redemptionsApi = inject(RedemptionsService);
  private readonly _activeRedemptionViewService = inject(ActiveRedemptionViewService);
  private readonly _analytics = inject(Analytics);
  private readonly _userView = inject(UserViewService);
  private readonly _paymentMethodsViewService = inject(PaymentMethodsViewService);
  private readonly _paymentMethodsService = inject(PaymentMethodsService);
  private readonly _accountLimitsView = inject(AccountLimitsViewService);
  private readonly _alert = inject(AlertService);
  private readonly _logger = inject(LOGGER_FACTORY).create('VenmoRedemption');

  // Input/Output Properties
  amount = input.required<number>();
  back = output<void>();
  close = output<void>();

  // Form Controls
  phoneNumber = new FormControl<string>('', [
    Validators.required,
    Validators.pattern(/^\d{10}$/), // US phone: exactly 10 digits
  ]);

  // Signals
  newVenmoAccount = signal(false);

  // Observable to Signal Conversions
  private readonly _allPaymentMethods = toSignal(this._paymentMethodsViewService.visiblePaymentMethods$);
  userState = toSignal(this._userView.state$);
  dailyRedemptionLimit = toSignal(this._accountLimitsView.dailyRedemptionLimits$);

  // Computed Signals
  nextAvailableDate = computed(() => formatDate(new Date(new Date().setUTCHours(24, 0, 0, 0)), 'short', 'en-US'));

  venmoPaymentMethod = computed(
    () =>
      this._allPaymentMethods()?.find(
        (p) =>
          p.payment_method_entity_type_id === PaymentMethodEntityTypeId.CoinflowVenmoAccount &&
          p.approved_by_payment_provider,
      ) as CoinflowVenmoPaymentMethod | undefined,
  );

  totalRedemptionAmount = computed(() =>
    decimal(this.amount())
      .minus(this.fee() ?? 2)
      .toNumber(),
  );

  dailyLimitExceeded = computed(() => {
    const dailyLimit = this.dailyRedemptionLimit();

    if (!dailyLimit) {
      return false;
    }

    const existingAmount = decimal(dailyLimit.daily_number);
    const limit = decimal(dailyLimit?.daily_redemptions_number_limit ?? 0);

    return existingAmount.add(1).gt(limit);
  });

  hasPlaythroughRequirement = computed(
    () =>
      !!this.userState()?.playthrough_requirement_id &&
      decimal(this.userState()?.playthrough_progress ?? 0).lt(decimal(this.userState()?.playthrough_requirement ?? 0)),
  );
  fees = computed(
    () =>
      Object.fromEntries(
        (this._fees() ?? []).map((f) => [f.speed, Number(f.feeAmountInTransactionCurrency)]),
      ) as Record<CoinflowWithdrawalSpeed, number>,
  );
  fee = computed(() => this.fees()?.['venmo'] ?? null);

  // Reactive Signals
  // Tracks the synchronous Coinflow redemption-method reconcile that runs on
  // init. 'syncing' blocks the picker behind a spinner; 'stale' unblocks with
  // the cached list (fail-open, payout-time revalidation is the backstop).
  pmSyncState = signal<'syncing' | 'fresh' | 'stale'>('syncing');
  linkingAccount = signal(false);
  submitting = signal(false);
  redemptionCreateInProgress = signal(false);
  loading = computed(() => this.pmSyncState() === 'syncing' || this.linkingAccount() || this.submitting());
  _fees = signal<CoinflowFeeEstimate[] | null>(null);

  // Constants
  idempotencyKey = generateId();

  async linkVenmoAccount() {
    if (this.phoneNumber.invalid) return;

    this.linkingAccount.set(true);
    try {
      await this._paymentMethodsService.linkVenmoAccount(this.phoneNumber.value!);
      // Realtime will automatically transition to confirmation view
      this._logger.info('Venmo account linked successfully');
      this.newVenmoAccount.set(false);
    } catch (err: any) {
      this._logger.error('Failed to link Venmo account', { error: err });
      this._alert.notifyDanger(
        'Failed to link Venmo',
        err.message || 'Unable to link Venmo account. Please try again.',
      );
    } finally {
      this.linkingAccount.set(false);
    }
  }

  async submit() {
    this.submitting.set(true);
  }

  // Trigger that opens once the (approved) Venmo payment method is ready and
  // the user has pressed submit. Emitting `null` keeps the gate closed.
  private readonly _submitTrigger = computed(() => {
    const paymentMethod = this.venmoPaymentMethod();

    return paymentMethod && paymentMethod.approved_by_payment_provider && this.submitting() ? paymentMethod : null;
  });

  constructor() {
    // Synchronously reconcile the user's saved Coinflow redemption methods
    // against Coinflow's live withdrawer state before they can pick a (possibly
    // deleted) token. The POST reconciled the DB; we then refresh the full
    // shared state via syncPaymentMethods() (GET-all) rather than
    // setPaymentMethods() so other methods aren't wiped. Once resolved we derive
    // the "new Venmo account" prompt from the refreshed list. Fail open:
    // stale:true or any error unblocks with the cached list.
    from(this._paymentMethodsService.syncCoinflowRedemptionPaymentMethods())
      .pipe(
        switchMap((response) =>
          from(this._paymentMethodsViewService.syncPaymentMethods()).pipe(
            map(() => (response.stale ? 'stale' : 'fresh')),
            catchError((err) => {
              this._logger.error('Failed to refresh payment methods after Coinflow sync', { error: err });
              return of('stale' as const);
            }),
          ),
        ),
        catchError((err) => {
          this._logger.error('Failed to sync Coinflow redemption payment methods', { error: err });
          return of('stale' as const);
        }),
        takeUntilDestroyed(),
      )
      .subscribe((state) => {
        this.pmSyncState.set(state);

        const hasVenmoAccount = this._allPaymentMethods()?.some(
          (p) => p.payment_method_entity_type_id === PaymentMethodEntityTypeId.CoinflowVenmoAccount,
        );

        if (!hasVenmoAccount) {
          this.newVenmoAccount.set(true);
        }
      });

    // Venmo fee estimate. switchMap keeps only the latest in-flight request as
    // the Venmo payment method resolves.
    toObservable(this.venmoPaymentMethod)
      .pipe(
        filter((paymentMethod) => !!paymentMethod),
        switchMap(() =>
          from(this._redemptionsApi.estimateCoinflowRedemptionFee(this.amount().toString())).pipe(
            catchError((err) => {
              this._logger.error('Failed to fetch Venmo fees', { error: err });
              if (err?.error?.code === 'PAYMENT_PROVIDER_BLOCKED') {
                const { title, message } = this._redemptionsApi.ErrorMap['PAYMENT_PROVIDER_BLOCKED'];
                this._alert.notifyDanger(title!, message!);
              }
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe((fees) => this._fees.set(fees));

    // Submit redemption when conditions met (similar to coinflow-redemption).
    // exhaustMap ignores further emissions while a create is in flight
    // (replaces redemptionCreateInProgress).
    toObservable(this._submitTrigger)
      .pipe(
        filter((paymentMethod): paymentMethod is NonNullable<typeof paymentMethod> => !!paymentMethod),
        exhaustMap((paymentMethod) => {
          this.redemptionCreateInProgress.set(true);

          return from(
            this._redemptionsApi.createRedemption(
              this.idempotencyKey,
              paymentMethod.payment_method_id,
              this.amount().toString(),
            ),
          ).pipe(
            tap((redemption) => {
              this._analytics.trackEvent(AnalyticsEvent.RedemptionInitiated, {
                $insert_id: redemption.redemption_id,
                value: this.amount(),
                currency: 'USD',
                provider: 'venmo',
                transaction_id: redemption.redemption_id,
              });

              this._activeRedemptionViewService.setActiveRedemption(redemption);
            }),
            catchError((err: any) => {
              const e = err as HttpErrorResponse;
              this.idempotencyKey = generateId();
              this._logger.error('Failed to create redemption', { error: err });

              if (isDevMode()) {
                console.error(err);
              }

              let errorMessage = e.error?.message ?? e.message;
              let errorTitle = 'Redemption Failed';

              if (e.error?.code) {
                const errHandle = this._redemptionsApi.ErrorMap?.[e?.error?.code];

                if (errHandle) {
                  errorMessage = errHandle.message;
                  errorTitle = errHandle.title;
                }
              }

              this._alert.notifyDanger(errorTitle, errorMessage);
              return EMPTY;
            }),
            finalize(() => {
              this.submitting.set(false);
              this.redemptionCreateInProgress.set(false);
            }),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }
}
